import useOnMount from 'hooks/useOnMount'
import { useUser } from 'services/UserProvider'
import useCrashLogger from '../../hooks/useCrashLogger'
import { userState } from '../../services/User/userState'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import {
  booksBannerManager,
  genericBannerManager,
} from '../../services/GenericBannerManager'
import brazeClient from '../../services/Analytics/platforms/braze.client'
import UserManager, { CAPTURE_EMAIL_SOURCE } from '../../services/UserManager'
import { getAmplitudeDeviceId } from '../../services/Analytics/platforms/amplitude'
import axios from 'axios'
import { useExperimentManager } from 'services/ExperimentManager/ExperimentManager'
import { ONE_DAY_IN_MILLISECONDS } from 'App.consts'
import { useLanguage } from 'services/LangProvider'
import { useKeys } from 'services/KeysProvider'
import { getRootLoaderData } from 'utils/remixClientUtils'
import { getLanguageAnalyticsProperties } from 'services/Analytics/languageAnalytics'
import { useIdleTimer } from 'react-idle-timer'

let didInitAnalytics = false

export const useInitClientServices = () => {
  const user = useUser()
  const language = useLanguage()
  const { buildTime } = useKeys()
  const experimentManager = useExperimentManager()
  const rootLoaderData = getRootLoaderData()

  if (!userState.getUser()) {
    userState.setUser(user)
  }

  const init = async () => {
    genericBannerManager.init()
    booksBannerManager.init()
  }
  useOnMount(() => {
    init()
  })

  if (!brazeClient.getBrazeInstance(false)) {
    brazeClient.initializeBraze(language)
  }

  if (!didInitAnalytics) {
    didInitAnalytics = true
    const initialEmail = UserManager.getUserEmail()
    analytics.init(user.deviceId, user.username, initialEmail).then(() => {
      // Sync Amplitude's deviceId to server if not already in session
      // This handles existing users who have an Amplitude deviceId in localStorage
      if (!user.deviceId) {
        const amplitudeDeviceId = getAmplitudeDeviceId()
        if (amplitudeDeviceId) {
          axios
            .post('/set-session', { deviceId: amplitudeDeviceId })
            .catch(() => {
              // Silently fail - not critical if sync fails
            })
        }
      }

      if (user?.unifiedUid) {
        const email = UserManager.getUserEmail() ?? user.initialBrazeEmail
        const emailConsent = email
          ? UserManager.hasExplicitEmailConsent(email)
          : null
        analytics.identifyUser(user.unifiedUid, email, emailConsent)

        // Set user email in analytics platforms if user came from initial_braze_id
        const urlParams = new URLSearchParams(window.location.search)
        if (urlParams.has('initial_braze_id') && email) {
          analytics.setUserEmail({
            email,
            isNewEmail: false,
            trackEventName: EVENT_NAMES.WEB_EMAIL_CAPTURED,
            trackProperties: {
              'User ID': user.unifiedUid,
              Source: CAPTURE_EMAIL_SOURCE.initialBrazeId,
            },
          })
        }
      }
      analytics.setUserProperties({
        ...(user?.username && { 'Initial User ID': user.username }),
        ...(user?.unifiedUid && { 'Unified UID': user.unifiedUid }),
        'Build Time': buildTime,
        'User Language': language,
        ...getLanguageAnalyticsProperties(rootLoaderData?.headerLanguage),
      })
      analytics.setActiveExperiments(experimentManager.getUserProperties())
    })
  }

  useCrashLogger()

  useRefreshOnIdle()
}

const useRefreshOnIdle = () => {
  const { getElapsedTime } = useIdleTimer({
    onActive: () => {
      analytics.track(EVENT_NAMES.APP_REFRESHED_AFTER_LONG_IDLE, {
        'Idle time in MS': getElapsedTime(),
      })
      setTimeout(() => {
        // added timeout to give analytics time to send the event
        location.reload()
      }, 2000)
    },
    timeout: ONE_DAY_IN_MILLISECONDS / 2,
  })
}
